import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface ModelSelectorProps {
  currentModel: string;
  onModelChange: (model: string) => void;
}

const AVAILABLE_MODELS = [
  { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini', icon: '⚡' },
  { id: 'gpt-4.1', name: 'GPT-4.1', icon: '🧠' },
  { id: 'fast', name: 'meta-llama/llama-3.3-70b-instruct', icon: '🤖' },
  { id: 'reasoning', name: 'GPT-OSS 120B (Cerebras)', icon: '💭' },
];

const ModelSelector: React.FC<ModelSelectorProps> = ({ currentModel, onModelChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (!buttonRef.current) return;
      
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = 150; // Approximate height of dropdown

      setDropdownPosition(spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top');
    };

    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen]);

  const currentModelInfo = AVAILABLE_MODELS.find(m => m.id === currentModel) || AVAILABLE_MODELS[0];

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center sm:justify-between px-2 sm:px-3 py-1.5 text-sm rounded-md border border-[var(--rhea-card-border)] bg-[var(--rhea-bg-primary)] hover:bg-[var(--rhea-bg-tertiary)] transition-all duration-200"
      >
        <div className="flex items-center gap-2 ">
          <span className="w-4 h-4 flex items-center justify-center text-sm">{currentModelInfo.icon}</span>
          <span className="text-[var(--rhea-text-primary)] hidden sm:block">{currentModelInfo.name}</span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-[var(--rhea-text-secondary)] transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div 
          className={`absolute ${
            dropdownPosition === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1'
          } left-0 min-w-48 sm:w-full bg-[var(--rhea-bg-primary)] border border-[var(--rhea-card-border)] rounded-md shadow-lg z-50`}
        >
          {AVAILABLE_MODELS.map((model) => (
            <button
              key={model.id}
              onClick={() => {
                onModelChange(model.id);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--rhea-bg-tertiary)] transition-colors flex items-center gap-2 ${
                model.id === currentModel ? 'text-[var(--rhea-accent-primary)]' : 'text-[var(--rhea-text-primary)]'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center text-sm">{model.icon}</span>
              {model.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModelSelector; 